import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HeroMorph } from '@/components/public/HeroMorph';
import { EASE, Reveal, fadeIn, CountUp } from './motion';
import { HERO, TRUST, useLang, pick } from './content';

const LOGOS = ['KDDI', 'Murata', 'Sony', 'Fujifilm', 'Mercari', 'GMO', 'Toyota Connected'];

export function HeroSection() {
  const lang = useLang();
  const reduce = useReducedMotion();
  const container: Variants | undefined = reduce
    ? undefined
    : { hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } };
  const item: Variants | undefined = reduce ? undefined : { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0 } };

  return (
    <section className="relative">
      <div aria-hidden className="absolute inset-x-0 top-0 h-[600px] paper-spotlight" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={reduce ? undefined : 'hidden'}
            animate={reduce ? undefined : 'visible'}
            variants={container}
          >
            <motion.div
              variants={item}
              transition={{ duration: 0.5, ease: EASE }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow-card border border-ink-100 text-[11px] uppercase tracking-[0.16em] font-medium text-ink-600"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500" />
              {pick(HERO.tag, lang)}
            </motion.div>

            <motion.h1
              variants={item}
              transition={{ duration: 0.6, ease: EASE }}
              className="mt-5 display serif-jp text-4xl md:text-5xl lg:text-[4.2rem] font-semibold tracking-tight text-ink-900 leading-[1.06] text-balance"
            >
              {pick(HERO.h1a, lang)} <span className="gradient-text">{pick(HERO.h1b, lang)}</span>
            </motion.h1>

            <motion.p
              variants={item}
              transition={{ duration: 0.6, ease: EASE }}
              className="mt-6 text-[16px] md:text-[17px] text-ink-600 leading-relaxed text-pretty max-w-2xl"
            >
              {pick(HERO.sub, lang)}
            </motion.p>

            <motion.div
              variants={item}
              transition={{ duration: 0.5, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <Link to="/signup" className="btn-press px-5 py-2.5 text-sm">
                {pick(HERO.ctaPrimary, lang)}
              </Link>
              <Link to="/demo" className="btn-ghost px-5 py-2.5 text-sm">
                {pick(HERO.ctaSecondary, lang)} →
              </Link>
              <span className="text-[12px] text-ink-400">{pick(HERO.microcopy, lang)}</span>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5">
            <HeroMorph />
          </div>
        </div>
      </div>
    </section>
  );
}

export function TrustStrip() {
  const lang = useLang();
  return (
    <Reveal variant={fadeIn}>
      <section className="border-y border-ink-100 bg-white/60">
        <div className="mx-auto max-w-6xl px-5 sm:px-6 py-7 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-400 font-semibold mb-2">
              {pick(TRUST.eyebrow, lang)}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {LOGOS.map((l) => (
                <span key={l} className="display text-ink-400 text-[15px] font-medium">{l}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-7 shrink-0">
            {TRUST.metrics.map((m) => (
              <div key={m.label} className="text-center">
                <CountUp to={m.value} suffix={m.suffix} className="text-2xl font-semibold text-ink-900" />
                <div className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-ink-400">{pick(m.label, lang)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-5 sm:px-6 pb-4 -mt-2">
          <p className="text-[10.5px] text-ink-300">{pick(TRUST.disclaimer, lang)}</p>
        </div>
      </section>
    </Reveal>
  );
}
